import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, Target, Building2, Contact, Briefcase, FileText, Calendar, Loader2 } from 'lucide-react';
import { api } from '../App';

const GlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Icon mapping
  const iconMap = {
    user: User,
    target: Target,
    building: Building2,
    contact: Contact,
    briefcase: Briefcase,
    'file-text': FileText,
    calendar: Calendar
  };

  // Color mapping by type
  const colorMap = {
    employee: 'bg-blue-100 text-blue-600',
    lead: 'bg-purple-100 text-purple-600',
    company: 'bg-teal-100 text-teal-600',
    contact: 'bg-green-100 text-green-600',
    project: 'bg-orange-100 text-orange-600',
    invoice: 'bg-red-100 text-red-600',
    proposal: 'bg-pink-100 text-pink-600',
    task: 'bg-indigo-100 text-indigo-600'
  };

  // Keyboard shortcut: Ctrl/Cmd + K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
        setResults([]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Arrow key navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && results[selectedIndex]) {
        e.preventDefault();
        handleResultClick(results[selectedIndex]);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex]);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await api.get(`/search?q=${encodeURIComponent(query)}&limit=5`);
        setResults(response.data.results || []);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (result) => {
    navigate(result.url);
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
    setResults([]);
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Search;
    return IconComponent;
  };

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Search Button */}
      <button
        onClick={handleOpen}
        className="hidden md:flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-slate-600 text-sm"
      >
        <Search className="h-4 w-4" />
        <span>Search...</span>
        <kbd className="hidden lg:inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded text-xs text-slate-500 border border-slate-200">
          <span>⌘</span>K
        </kbd>
      </button>

      {/* Mobile Search Icon */}
      <button
        onClick={handleOpen}
        className="md:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
      >
        <Search className="h-5 w-5 text-slate-600" />
      </button>

      {/* Search Modal/Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/20 z-40" onClick={handleClose} />

          {/* Search Container */}
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 w-full max-w-2xl z-50 px-4">
            <div className="bg-white rounded-lg shadow-2xl border border-slate-200">
              {/* Search Input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
                <Search className="h-5 w-5 text-slate-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search employees, leads, companies, projects..."
                  className="flex-1 outline-none text-slate-900 placeholder-slate-400"
                />
                {loading && <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />}
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              </div>

              {/* Results */}
              <div className="max-h-96 overflow-y-auto">
                {query.length < 2 ? (
                  <div className="px-4 py-8 text-center text-slate-500">
                    <Search className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Type at least 2 characters to search</p>
                    <p className="text-xs mt-2 text-slate-400">
                      Search across employees, leads, companies, contacts, projects, invoices, and more
                    </p>
                  </div>
                ) : loading ? (
                  <div className="px-4 py-8 text-center text-slate-500">
                    <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin text-slate-400" />
                    <p className="text-sm">Searching...</p>
                  </div>
                ) : results.length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-500">
                    <Search className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">No results found for "{query}"</p>
                    <p className="text-xs mt-2 text-slate-400">
                      Try different keywords or check spelling
                    </p>
                  </div>
                ) : (
                  <div className="py-2">
                    {Object.entries(groupedResults).map(([type, items]) => (
                      <div key={type} className="mb-4 last:mb-0">
                        <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {type}s ({items.length})
                        </div>
                        {items.map((result, index) => {
                          const globalIndex = results.indexOf(result);
                          const IconComponent = getIcon(result.icon);
                          const isSelected = globalIndex === selectedIndex;

                          return (
                            <button
                              key={result.id}
                              onClick={() => handleResultClick(result)}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${
                                isSelected ? 'bg-slate-50' : ''
                              }`}
                            >
                              <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[result.type]}`}>
                                <IconComponent className="h-5 w-5" />
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <div className="font-medium text-slate-900 truncate">
                                  {result.title}
                                </div>
                                <div className="text-sm text-slate-500 truncate">
                                  {result.subtitle}
                                </div>
                              </div>
                              {isSelected && (
                                <kbd className="px-2 py-1 bg-slate-200 rounded text-xs text-slate-600">
                                  ↵
                                </kbd>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200">↑</kbd>
                    <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200">↓</kbd>
                    <span>to navigate</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200">↵</kbd>
                    <span>to select</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white rounded border border-slate-200">esc</kbd>
                    <span>to close</span>
                  </div>
                </div>
                <div>
                  {results.length > 0 && (
                    <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GlobalSearch;
